import { usePartner } from "../../context/PartnerContext";
import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import usePageTitle from "../../hooks/usePageTitle";

const C = {
  bg:"var(--bg)", card:"var(--bg-surface)", border:"var(--border)",
  text:"var(--text-main)", muted:"var(--text-muted)", faint:"var(--text-faint)",
  accent:"var(--accent)", success:"var(--success)", danger:"var(--danger)", warning:"var(--warning)",
};
const gradeColor = g => g?.startsWith("A")?"var(--success)":g?.startsWith("B")?"var(--accent)":g?.startsWith("C")?"var(--warning)":"var(--danger)";
const fmt2 = v => { const n=Number(v)||0; if(Math.abs(n)>=1000000) return (n/1000000).toFixed(2)+"M"; if(Math.abs(n)>=1000) return (n/1000).toFixed(2)+"K"; return n.toFixed(2); };
const fmtPct = v => (Number(v)||0).toFixed(1)+"%";

function MiniEquityChart({ points }) {
  if (!points?.length) return null;
  const vals = points.map(p => p.equity || p.balance || 0);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const W = 420, H = 80;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 8) - 4;
    return `${x},${y}`;
  }).join(" ");
  const isUp = vals[vals.length-1] >= vals[0];
  const color = isUp ? "rgba(34,197,94,1)" : "rgba(239,68,68,1)";
  const fill  = isUp ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.08)";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:80,display:"block"}}>
      <defs>
        <linearGradient id="ecg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <polygon points={`0,${H} ${pts} ${W},${H}`} fill="url(#ecg)"/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
    </svg>
  );
}

function BlurLock({ label }) {
  return (
    <div style={{position:"relative",borderRadius:10,overflow:"hidden",border:`1px solid ${C.border}`,background:C.card}}>
      <div style={{filter:"blur(4px)",opacity:0.4,padding:16,fontSize:12,color:C.muted,lineHeight:2}}>
        {[1,2,3,4].map(i=>(
          <div key={i} style={{height:12,background:"var(--border)",borderRadius:4,marginBottom:8,width:["80%","60%","90%","70%"][i-1]}}/>
        ))}
      </div>
      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8}}>
        <div style={{fontSize:18}}></div>
        <div style={{fontSize:12,fontWeight:700,color:C.text}}>{label}</div>
        <div style={{fontSize:11,color:C.muted}}>Register free to unlock</div>
      </div>
    </div>
  );
}

export default function PublicStrategyPage() {
  const { accountId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const refCode = searchParams.get("ref") || "";

  const [data, setData] = useState(null);
  const [curve, setCurve] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [period, setPeriod] = useState(0);

  const { appName, tagline } = usePartner();
  usePageTitle(data ? `${data.nickname || data.traderName || "Strategy"}` : "Strategy");

  // Save ref code to localStorage for register tracking
  useEffect(() => {
    if (refCode) localStorage.setItem("ca_ref", refCode);
  }, [refCode]);

  useEffect(() => {
    Promise.all([
      fetch(`/api/public/trader/${accountId}`).then(r=>r.json()),
      fetch(`/api/public/traders`).then(r=>r.json()),
    ]).then(([detail, list]) => {
      if (detail.ok) {
        // Enrich with nickname from public traders list
        const match = (list.traders||[]).find(t => t.id === accountId);
        if (match) {
          detail.nickname    = match.nickname || match.traderName || match.name;
          detail.broker      = match.broker;
          detail.platform    = match.platform;
          detail.accountAge  = match.accountAge;
        }
        setData(detail);
      }
    }).catch(() => {}).finally(() => setLoading(false));
    fetch(`/api/public/trader-equity-curve?account_id=${accountId}${period>0?'&days='+period:''}`)
      .then(r => r.json())
      .then(d => { if (d.points) setCurve(d); })
      .catch(() => {});
  }, [accountId, period]);

  const registerUrl = `/register${refCode ? `?ref=${refCode}` : ""}`;

  const copyShareUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",color:C.muted,fontSize:14}}>
      Loading strategy...
    </div>
  );

  if (!data) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12}}>
      <div style={{fontSize:32}}></div>
      <div style={{fontSize:16,fontWeight:700,color:C.text}}>Strategy not found</div>
      <button onClick={()=>navigate("/")} style={{padding:"8px 20px",borderRadius:8,background:C.accent,color:"#fff",border:"none",cursor:"pointer",fontSize:13}}>Back to Home</button>
    </div>
  );

  const gc = gradeColor(data.grade);
  const wr = data.winRate || 0;

  return (
    <div style={{minHeight:"100vh",background:C.bg,color:C.text}}>

      {/* TOP NAV */}
      <header style={{background:"var(--bg-surface)",borderBottom:`1px solid ${C.border}`,padding:"0 24px",height:54,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:50}}>
        <a href="/" style={{textDecoration:"none"}}>
          <div style={{fontWeight:800,fontSize:16,color:C.text}}>{appName === "CrunchAlpha" ? (<>Crunch<span style={{color:C.accent}}>Alpha</span></>) : appName}</div>
          {tagline && <div style={{fontSize:9,color:C.accent,fontWeight:600}}>{tagline}</div>}
        </a>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <button onClick={copyShareUrl}
            style={{padding:"6px 14px",borderRadius:7,background:"var(--bg-elevated)",border:`1px solid ${C.border}`,color:C.muted,fontSize:12,cursor:"pointer"}}>
            {copied ? " Copied!" : " Share"}
          </button>
          <a href={registerUrl}
            style={{padding:"7px 16px",borderRadius:7,background:C.accent,color:"#fff",fontSize:13,fontWeight:600,textDecoration:"none"}}>
            Get started free →
          </a>
        </div>
      </header>

      <div style={{maxWidth:780,margin:"0 auto",padding:"28px 20px 48px"}}>

        {/* STRATEGY HEADER */}
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"24px 24px 20px",marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                <div style={{fontSize:22,fontWeight:800}}>{data.nickname || data.traderName || "Strategy"}</div>
                <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,background:"rgba(34,197,94,0.1)",color:"var(--success)",border:"1px solid rgba(34,197,94,0.25)"}}>
                     Live Trader
                </span>
              </div>
              <div style={{fontSize:12,color:C.muted}}>
                {[data.broker, data.platform, data.accountAge ? `Trading for ${data.accountAge}` : null, `${data.totalTrades} trades`].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:10,color:C.faint,marginBottom:3}}>AlphaScore</div>
              <div style={{width:68,height:68,borderRadius:"50%",border:`4px solid ${gc}`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"rgba(255,255,255,0.02)"}}>
                <div style={{fontSize:16,fontWeight:900,color:gc}}>{data.grade}</div>
                <div style={{fontSize:11,fontWeight:700,color:gc}}>{fmt2(data.alphaScore)}</div>
              </div>
              <div style={{fontSize:10,color:C.faint,marginTop:5}}>
                Risk: <span style={{fontWeight:700,color:C.muted}}>{data.riskLevel}</span>
              </div>
            </div>
          </div>

          {/* Win Rate bar */}
          <div style={{margin:"16px 0 4px"}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.faint,marginBottom:5}}>
              <span>Win Rate</span><span style={{fontWeight:700,color:C.text}}>{fmtPct(wr)}</span>
            </div>
            <div style={{height:5,background:"var(--bg-elevated)",borderRadius:999,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${wr}%`,background:C.accent,borderRadius:999}}/>
            </div>
          </div>
        </div>

        {/* KEY METRICS */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:16}}>
          {[
            { l:"ROI",      v:(data.roi>=0?"+":"")+fmtPct(data.roi), color:data.roi>=0?"var(--success)":"var(--danger)" },
            { l:"Max DD",   v:fmtPct(data.maxDD),                    color:data.maxDD>30?"var(--danger)":data.maxDD>15?"var(--warning)":"var(--success)" },
            { l:"Win Rate", v:fmtPct(data.winRate),                  color:C.text },
            { l:"Trades",   v:String(data.totalTrades),               color:C.text },
          ].map(m => (
            <div key={m.l} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 14px"}}>
              <div style={{fontSize:10,color:C.faint,marginBottom:4}}>{m.l}</div>
              <div style={{fontSize:20,fontWeight:800,color:m.color}}>{m.v}</div>
            </div>
          ))}
        </div>

        {/* EQUITY CURVE */}
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
            <div style={{fontSize:13,fontWeight:700}}>Equity Growth</div>
            <div style={{display:"flex",gap:4}}>
              {[{l:"1M",v:30},{l:"3M",v:90},{l:"1Y",v:365},{l:"All",v:0}].map(opt=>(
                <button key={opt.l} onClick={()=>setPeriod(opt.v)}
                  style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${period===opt.v?"var(--accent)":"var(--border)"}`,
                    background:period===opt.v?"var(--accent-soft)":"transparent",
                    color:period===opt.v?"var(--accent)":"var(--text-faint)",
                    fontSize:11,cursor:"pointer",fontWeight:period===opt.v?700:400}}>
                  {opt.l}
                </button>
              ))}
            </div>
            {curve && <div style={{fontSize:12,color:C.muted}}>
              Start: ${fmt2(curve.start)} → Current: ${fmt2(curve.end)} &nbsp;
              <span style={{color:curve.growth_pct>=0?"var(--success)":"var(--danger)",fontWeight:700}}>
                {curve.growth_pct>=0?"+":""}{fmt2(curve.growth_pct)}%
              </span>
            </div>}
          </div>
          {curve?.points ? <MiniEquityChart points={curve.points}/> :
            <div style={{height:80,display:"flex",alignItems:"center",justifyContent:"center",color:C.faint,fontSize:12}}>No equity data yet</div>}
        </div>

        {/* ALPHARANK PILLARS */}
        {data.pillars?.length > 0 && (
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>AlphaRank™ Breakdown</div>
            {data.pillars.map(p => (
              <div key={p.code} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:12,color:C.muted}}>{p.name}</span>
                  <span style={{fontSize:12,fontWeight:700,color:C.text}}>{p.score?.toFixed(0)}</span>
                </div>
                <div style={{height:4,background:"var(--bg-elevated)",borderRadius:999,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${p.score}%`,background:C.accent,borderRadius:999,opacity:0.7+p.score/333}}/>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* RISK FLAGS */}
        {data.flags?.length > 0 && (
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,marginBottom:12}}>Risk Flags</div>
            {data.flags.map((f,i) => (
              <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:10,
                padding:"10px 12px",borderRadius:8,
                background:f.severity==="MAJOR"?"rgba(239,68,68,0.06)":"rgba(234,179,8,0.06)",
                border:`1px solid ${f.severity==="MAJOR"?"rgba(239,68,68,0.2)":"rgba(234,179,8,0.2)"}`}}>
                <span style={{fontSize:14,flexShrink:0,marginTop:1}}>{f.severity==="MAJOR"?"    ":"ℹ  "}</span>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:2}}>{f.soft_title||f.title}</div>
                  <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>{f.soft_desc||f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* LOCKED SECTIONS */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20}}>
          <BlurLock label="Full Trade History"/>
          <BlurLock label="Monthly P&L Breakdown"/>
        </div>

        {/* CTA */}
        <div style={{background:"rgba(78,154,241,0.07)",border:"1px solid rgba(78,154,241,0.2)",borderRadius:14,padding:"32px 24px",textAlign:"center"}}>
          <div style={{fontSize:20,fontWeight:800,marginBottom:8,letterSpacing:"-0.02em"}}>
            Follow this strategy
          </div>
          <div style={{fontSize:13,color:C.muted,lineHeight:1.7,maxWidth:360,margin:"0 auto 20px"}}>
            Copy trades automatically with risk-normalized lot sizing. Your funds stay in your own broker — always.
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
            <a href={registerUrl}
              style={{padding:"12px 28px",borderRadius:8,background:C.accent,color:"#fff",fontSize:14,fontWeight:700,textDecoration:"none"}}>
              Register free to follow →
            </a>
            <a href="/"
              style={{padding:"11px 20px",borderRadius:8,background:"transparent",border:`1px solid ${C.border}`,color:C.muted,fontSize:13,textDecoration:"none"}}>
              Learn more
            </a>
          </div>
          <div style={{fontSize:11,color:C.faint,marginTop:12}}>
            Performance fee only when you profit · Non-custodial · Free to join
          </div>
        </div>

      </div>

      {/* FOOTER */}
      <footer style={{borderTop:`1px solid ${C.border}`,padding:"16px 24px",textAlign:"center",fontSize:11,color:C.faint}}>
        <strong style={{color:C.text}}>Crunch<span style={{color:C.accent}}>Alpha</span></strong>
        {" "}© 2026 · Non-custodial · Trading involves risk ·{" "}
        <a href="/legal/risk" style={{color:C.faint,textDecoration:"none"}}>Risk Disclosure</a>
      </footer>

    </div>
  );
}
